/**
 * RAID Log Component
 * Displays and manages RAID items (Risks, Assumptions, Issues, Dependencies)
 */
import React, { useState, useEffect } from 'react'
import { AlertTriangle, Plus, Filter, Clock, AlertCircle, XCircle, X } from 'lucide-react'
import { programRAIDService, ProgramRAID, RAIDType, RAIDStatus, Priority, RAIDCreate } from '../../services/programRAID'
import { toast } from 'sonner'
import { getErrorMessage } from '../../utils/apiTransformers'
import type { ApiErrorLike } from '../../utils/apiError'

interface RAIDLogProps {
  programId: number
}

const EMPTY_RAID: Omit<RAIDCreate, 'program_id'> = {
  type: 'risk',
  title: '',
  description: '',
  priority: 'medium',
  status: 'open',
  impact: '',
  mitigation_plan: '',
}

export default function RAIDLog({ programId }: RAIDLogProps) {
  const [raidItems, setRaidItems] = useState<ProgramRAID[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterPriority, setFilterPriority] = useState<string>('all')
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState<Omit<RAIDCreate, 'program_id'>>(EMPTY_RAID)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadRAIDItems()
  }, [programId, filterType, filterStatus, filterPriority])

  const loadRAIDItems = async () => {
    setLoading(true)
    try {
      const data = await programRAIDService.list(
        programId,
        filterType !== 'all' ? filterType as RAIDType : undefined,
        filterStatus !== 'all' ? filterStatus as RAIDStatus : undefined,
        filterPriority !== 'all' ? filterPriority as Priority : undefined
      )
      setRaidItems(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading RAID items:', error)
      toast.error(getErrorMessage(error) || 'Failed to load RAID items')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) { toast.error('Title is required'); return }
    setSaving(true)
    try {
      await programRAIDService.create(programId, { ...formData, program_id: programId })
      toast.success('RAID item created')
      setShowModal(false)
      setFormData(EMPTY_RAID)
      loadRAIDItems()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to create RAID item')
    } finally {
      setSaving(false)
    }
  }

  const getTypeIcon = (type: RAIDType) => {
    switch (type) {
      case 'risk':
        return <AlertTriangle className="w-5 h-5 text-red-600" />
      case 'issue':
        return <XCircle className="w-5 h-5 text-orange-600" />
      case 'assumption':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />
      case 'dependency':
        return <Clock className="w-5 h-5 text-blue-600" />
    }
  }

  const getTypeColor = (type: RAIDType) => {
    switch (type) {
      case 'risk':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'issue':
        return 'bg-orange-100 text-orange-700 border-orange-200'
      case 'assumption':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'dependency':
        return 'bg-blue-100 text-blue-700 border-blue-200'
    }
  }

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low':
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getStatusColor = (status: RAIDStatus) => {
    switch (status) {
      case 'open':
        return 'bg-gray-100 text-gray-700 border-gray-200'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'resolved':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'closed':
        return 'bg-green-100 text-green-700 border-green-200'
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-[#f0f4f8] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const openItems = raidItems.filter(item => item.status === 'open' || item.status === 'in_progress')
  const criticalItems = raidItems.filter(item => item.priority === 'critical' && item.status !== 'resolved' && item.status !== 'closed')

  return (
    <>
    <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-[#102a43] mb-1">RAID Log</h3>
          <div className="flex items-center gap-4 text-sm text-[#829ab1]">
            <span>Total: {raidItems.length}</span>
            {openItems.length > 0 && (
              <span className="text-blue-600 font-medium">Open: {openItems.length}</span>
            )}
            {criticalItems.length > 0 && (
              <span className="text-red-600 font-medium">Critical: {criticalItems.length}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          New RAID Item
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#829ab1]" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
          >
            <option value="all">All Types</option>
            <option value="risk">Risks</option>
            <option value="assumption">Assumptions</option>
            <option value="issue">Issues</option>
            <option value="dependency">Dependencies</option>
          </select>
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
        >
          <option value="all">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* RAID Items List */}
      {raidItems.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-[#829ab1] mx-auto mb-4" />
          <p className="text-[#829ab1]">No RAID items found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {raidItems.map((item) => (
            <div
              key={item.id}
              className="p-4 border border-[#bcccdc] rounded-lg hover:border-[#486581] hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getTypeIcon(item.type)}
                    <h4 className="font-medium text-[#102a43]">{item.title}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getTypeColor(item.type)} capitalize`}>
                      {item.type}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getPriorityColor(item.priority)} capitalize`}>
                      {item.priority}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                  {item.description && (
                    <p className="text-sm text-[#829ab1] mb-2">{item.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-[#829ab1]">
                    {item.resolution_date && (
                      <span>Resolved: {new Date(item.resolution_date).toLocaleDateString()}</span>
                    )}
                    {item.project_id && (
                      <span>Project ID: {item.project_id}</span>
                    )}
                    <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                  {item.mitigation_plan && (
                    <div className="mt-2 p-2 bg-[#f0f4f8] rounded text-xs text-[#102a43]">
                      <span className="font-medium">Mitigation:</span> {item.mitigation_plan}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Create RAID Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#102a43]">New RAID Item</h2>
            <button onClick={() => { setShowModal(false); setFormData(EMPTY_RAID) }} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                <select value={formData.type} onChange={e => setFormData(p => ({ ...p, type: e.target.value as RAIDType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="risk">Risk</option>
                  <option value="assumption">Assumption</option>
                  <option value="issue">Issue</option>
                  <option value="dependency">Dependency</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority *</label>
                <select value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: e.target.value as Priority }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea value={formData.description || ''} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] resize-none text-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Impact</label>
              <input type="text" value={formData.impact || ''} onChange={e => setFormData(p => ({ ...p, impact: e.target.value }))}
                placeholder="e.g. High financial impact"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mitigation Plan</label>
              <textarea value={formData.mitigation_plan || ''} onChange={e => setFormData(p => ({ ...p, mitigation_plan: e.target.value }))}
                rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] resize-none text-gray-900" />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <button type="button" onClick={() => { setShowModal(false); setFormData(EMPTY_RAID) }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] disabled:opacity-50 flex items-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  )
}

